import React, { useRef, useState, useEffect, useCallback } from "react";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import SEO from "../../components/SEO";
import { usePlausible } from "../../hooks/usePlausible";
import {
  PREMIUM_PAGE_VIEW,
  JOIN_PREMIUM,
  JOIN_PREMIUM_PAGE,
  trackButtonClick,
} from "../../utils/analytics";
import styles from "./Premium.module.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import Spline from "@splinetool/react-spline";
import PremiumSection from "../../components/PremiumSection";
import { withAssetPath, buildCanonicalUrl } from "../../utils/basePath";

gsap.registerPlugin(ScrollTrigger, useGSAP);

const PREMIUM_ASSET_PATHS = {
  bg2: withAssetPath("/assets/images/bg2.webp"),
  bg3: withAssetPath("/assets/images/bg3.webp"),
  f1: withAssetPath("/assets/images/f1.png"),
  f2: withAssetPath("/assets/images/f2.png"),
  f3: withAssetPath("/assets/images/f3.png"),
  f4: withAssetPath("/assets/images/f4.png"),
  f5: withAssetPath("/assets/images/f5.png"),
  f6: withAssetPath("/assets/images/f6.png"),
  f7: withAssetPath("/assets/images/f7.png"),
  f8: withAssetPath("/assets/images/f8.png"),
  vc1: withAssetPath("/assets/images/vc1.png"),
  app: withAssetPath("/assets/images/app.png"),
  album1: withAssetPath("/assets/images/album1.webp"),
  album2: withAssetPath("/assets/images/album2.webp"),
  album3: withAssetPath("/assets/images/album3.webp"),
  album4: withAssetPath("/assets/images/album4.webp"),
  album5: withAssetPath("/assets/images/album5.webp"),
  album6: withAssetPath("/assets/images/album6.webp"),
  album7: withAssetPath("/assets/images/album7.webp"),
  album8: withAssetPath("/assets/images/album8.webp"),
  album9: withAssetPath("/assets/images/album9.webp"),
  album10: withAssetPath("/assets/images/album10.webp"),
  hendrix: withAssetPath("/assets/images/hendrix.png"),
  petty: withAssetPath("/assets/images/petty.png"),
  morrison: withAssetPath("/assets/images/morrison.png"),
  bowie: withAssetPath("/assets/images/bowie.png"),
  lennon: withAssetPath("/assets/images/lennon.png"),
};

const Premium: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(
    new Set()
  );

  const mainContentRef = useRef<HTMLDivElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const splineRef = useRef<HTMLDivElement>(null);
  const trackEvent = usePlausible();

  // Track premium page view on component mount
  useEffect(() => {
    trackEvent(PREMIUM_PAGE_VIEW);
  }, [trackEvent]);

  const handleImageError = useCallback((src: string) => {
    setImageLoadErrors((prev) => new Set(prev).add(src));
  }, []);

  const handleImageLoad = useCallback((src: string) => {
    setImageLoadErrors((prev) => {
      const newSet = new Set(prev);
      newSet.delete(src);
      return newSet;
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const criticalImages = [PREMIUM_ASSET_PATHS.bg2, PREMIUM_ASSET_PATHS.bg3];

    criticalImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (parallaxRef.current) {
      const heroOffset = scrollY * 0.5;
      parallaxRef.current.style.transform = `translateY(${heroOffset}px)`;
    }
  }, [scrollY]);

  // Note: Removed GSAP animations for commented out elements (.parallax-bg-img, .now-playing-card)
  // to eliminate console warnings. Re-enable when those elements are uncommented in JSX.

  useGSAP(
    () => {
      gsap.set(".feature-container", {
        y: 150,
        opacity: 1,
        scale: 0.9,
        transformOrigin: "center center",
      });

      gsap.utils.toArray(".feature-container").forEach((container: any) => {
        ScrollTrigger.create({
          trigger: container,
          start: "top bottom",
          end: "center center",
          scrub: 1,
          animation: gsap.to(container, {
            y: 0,
            opacity: 1,
            scale: 1,
            ease: "none",
          }),
        });
      });

      // Premium Features Smooth Entrance Animation
      gsap.set(".premium-feature", {
        y: 60,
        opacity: 0,
        scale: 0.85,
        rotateY: 8,
        transformOrigin: "center center",
      });

      gsap.set(".premium-feature-icon", {
        scale: 0.8,
        opacity: 0.7,
      });

      gsap.set(".feature-ellipse", {
        scale: 0.8,
        opacity: 0.2,
      });

      // Create a master timeline for smooth coordinated animation
      const premiumTl = gsap.timeline({
        scrollTrigger: {
          trigger: ".premium-features-container",
          start: "top 85%",
          end: "bottom center",
          toggleActions: "play none none reverse",
        },
      });

      // Staggered entrance with coordinated scaling
      premiumTl
        .to(".premium-feature", {
          y: 0,
          opacity: 1,
          scale: 1,
          rotateY: 0,
          duration: 1.2,
          stagger: {
            amount: 0.8,
            ease: "power2.out",
          },
          ease: "power3.out",
        })
        .to(
          ".premium-feature-icon",
          {
            scale: 1,
            opacity: 1,
            duration: 0.8,
            stagger: {
              amount: 0.6,
              ease: "power2.out",
            },
            ease: "back.out(1.2)",
          },
          "-=0.6"
        )
        .to(
          ".feature-ellipse",
          {
            scale: 1,
            opacity: 0.3,
            duration: 1,
            stagger: {
              amount: 0.8,
              ease: "power2.out",
            },
            ease: "power2.out",
          },
          "-=0.8"
        );

      // Delayed floating animation - starts after entrance completes
      gsap.delayedCall(2, () => {
        gsap.to(".premium-feature-icon", {
          y: -8,
          duration: 2.5,
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          stagger: 0.4,
        });
      });

      // Delayed ellipse pulse - starts after entrance completes
      gsap.delayedCall(2.5, () => {
        gsap.to(".feature-ellipse", {
          scale: 1.05,
          opacity: 0.4,
          duration: 4,
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          stagger: 0.6,
        });
      });
    },
    { scope: mainContentRef }
  );
  return (
    <>
      <SEO
        title="Rythm Premium - Unlimited Music, Higher Quality Audio & Premium Features"
        description="Upgrade to Rythm Premium for unlimited queue length, 320kbps audio quality, unlimited autoplay, and share premium features with everyone in your Discord session. Only $4.99/month."
        canonicalUrl={buildCanonicalUrl("/premium")}
        keywords="rythm premium, discord music bot premium, unlimited queue, high quality audio, 320kbps, discord music, premium music bot, shared premium benefits"
        schemaType="Product"
        price="4.99"
        currency="USD"
        ogImage="https://rythm.fm/assets/images/premium-og.png"
        ogImageAlt="Rythm Premium - $4.99/month for unlimited music and premium features"
        ogType="product"
      />

      <div className={styles.premium}>
        <Header />
        <div ref={mainContentRef} className={styles.mainContent}>
          <div className={`${styles.heroContainter} hero-container`}>
            <div ref={splineRef} className={styles.play}>
              <Spline scene="https://prod.spline.design/j4nma5SrCg0u6BZ1/scene.splinecode" />
            </div>
          </div>
          <div className={styles.heroText}>
            <h1>Rythm Premium</h1>
            <p className={styles.heroSubtitle}>
              The best bot on discord keeps getting better. Premium is the best
              way to support the bot and your favorite artists. Share premium
              with every person in your session and enjoy the best music
              experience on discord.
            </p>
            <div className={styles.heroButton}>
              <button
                onClick={() => {
                  trackButtonClick(
                    trackEvent,
                    JOIN_PREMIUM,
                    JOIN_PREMIUM_PAGE,
                    () => {
                      window.open(
                        "https://premium.rythm.fm/",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }
                  );
                }}
                aria-label="Join Premium - opens in new tab"
              >
                Join Premium
              </button>
            </div>
          </div>
          <div
            className={`${styles.premiumFeatures} premium-features-container`}
          >
            {/* Unlimited Autoplay */}
            <div className={`${styles.premiumFeature} premium-feature`}>
              <div
                className={`${styles.ellipse} ${styles.yellowEllipse} feature-ellipse`}
              ></div>
              <div
                className={`${styles.premiumFeatureIcon} premium-feature-icon`}
              >
                <img
                  className={styles.premiumFeatureIconImg}
                  src={PREMIUM_ASSET_PATHS.f3}
                  alt="Unlimited Autoplay"
                  loading="eager"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.f3)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.f3)}
                />
              </div>
              <div className={styles.premiumDescription}>
                <h2>Ad-free Unlimited Listening</h2>
                <p>Enjoy uninterrupted listening without any advertisements.</p>
              </div>
            </div>

            {/* All Platforms */}
            <div className={`${styles.premiumFeature} premium-feature`}>
              <div
                className={`${styles.ellipse} ${styles.pinkEllipse} feature-ellipse`}
              ></div>
              <div
                className={`${styles.premiumFeatureIcon} premium-feature-icon`}
              >
                <img
                  className={styles.premiumFeatureIconImg}
                  src={PREMIUM_ASSET_PATHS.f6}
                  alt="All Platforms"
                  loading="eager"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.f6)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.f6)}
                />
              </div>
              <div className={styles.premiumDescription}>
                <h2>All Platforms</h2>
                <p>
                  Use Rythm Premium on any platform including the website, app,
                  and more.
                </p>
              </div>
            </div>
            {/* Higher Audio Quality */}
            <div className={`${styles.premiumFeature} premium-feature`}>
              <div
                className={`${styles.ellipse} ${styles.blueEllipse} feature-ellipse`}
              ></div>
              <div
                className={`${styles.premiumFeatureIcon} premium-feature-icon`}
              >
                <img
                  className={styles.premiumFeatureIconImg}
                  src={PREMIUM_ASSET_PATHS.f1}
                  alt="Higher Audio Quality"
                  loading="eager"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.f1)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.f1)}
                />
              </div>
              <div className={styles.premiumDescription}>
                <h2>Higher Audio Quality </h2>
                <p>
                  (320kbps) Enjoy better audio for everyone using Rythm when you
                  play.
                </p>
              </div>
            </div>
            {/* Seamless Crossfade */}
            <div className={`${styles.premiumFeature} premium-feature`}>
              <div
                className={`${styles.ellipse} ${styles.orangeEllipse} feature-ellipse`}
              ></div>
              <div
                className={`${styles.premiumFeatureIcon} premium-feature-icon`}
              >
                <img
                  className={styles.premiumFeatureIconImg}
                  src={PREMIUM_ASSET_PATHS.f8}
                  alt="Seamless Crossfade"
                  loading="eager"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.f8)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.f8)}
                />
              </div>
              <div className={styles.premiumDescription}>
                <h2>Seamless Crossfade</h2>
                <p>
                  Smooth transitions between song for uninterrupted listening.
                </p>
              </div>
            </div>
            {/* Audio Effects Studio */}
            <div className={`${styles.premiumFeature} premium-feature`}>
              <div
                className={`${styles.ellipse} ${styles.pinkEllipse} feature-ellipse`}
              ></div>
              <div
                className={`${styles.premiumFeatureIcon} premium-feature-icon`}
              >
                <img
                  className={styles.premiumFeatureIconImg}
                  src={PREMIUM_ASSET_PATHS.f7}
                  alt="Audio Effects Studio"
                  loading="eager"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.f7)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.f7)}
                />
              </div>
              <div className={styles.premiumDescription}>
                <h2>Audio Effects Studio</h2>
                <p>
                  Transform your music with Bass Boost, Speed, Pitch, Nightcore
                  & more.
                </p>
              </div>
            </div>

            {/* Unlimited Queue */}
            <div className={`${styles.premiumFeature} premium-feature`}>
              <div
                className={`${styles.ellipse} ${styles.purpleEllipse} feature-ellipse`}
              ></div>
              <div
                className={`${styles.premiumFeatureIcon} premium-feature-icon`}
              >
                <img
                  className={styles.premiumFeatureIconImg}
                  src={PREMIUM_ASSET_PATHS.f2}
                  alt="Unlimited Queue Length"
                  loading="eager"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.f2)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.f2)}
                />
              </div>
              <div className={styles.premiumDescription}>
                <h2>Unlimited Queue</h2>
                <p>No limit on building 1,000+ song queues.</p>
              </div>
            </div>

            {/* Share Premium */}
            <div className={`${styles.premiumFeature} premium-feature`}>
              <div
                className={`${styles.ellipse} ${styles.greenEllipse} feature-ellipse`}
              ></div>
              <div
                className={`${styles.premiumFeatureIcon} premium-feature-icon`}
              >
                <img
                  className={styles.premiumFeatureIconImg}
                  src={PREMIUM_ASSET_PATHS.f4}
                  alt="Share Benefits"
                  loading="eager"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.f4)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.f4)}
                />
              </div>
              <div className={styles.premiumDescription}>
                <h2>Share Premium</h2>
                <p>
                  Premium is activated for everyone in the sessions while you're
                  around.
                </p>
              </div>
            </div>
            {/* Support Artists */}
            <div className={`${styles.premiumFeature} premium-feature`}>
              <div
                className={`${styles.ellipse} ${styles.redEllipse} feature-ellipse`}
              ></div>
              <div
                className={`${styles.premiumFeatureIcon} premium-feature-icon`}
              >
                <img
                  className={styles.premiumFeatureIconImg}
                  src={PREMIUM_ASSET_PATHS.f5}
                  alt="Support Artists"
                  loading="eager"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.f5)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.f5)}
                />
              </div>
              <div className={styles.premiumDescription}>
                <h2>Support Artists</h2>
                <p>
                  Support your favorite artists and new features across all
                  platforms.
                </p>
              </div>
            </div>
          </div>
          <PremiumSection />
          <div className={styles.featureFocus}>
            <div className={styles.featureLeft}>
              <div className={styles.featureHero}>
                <div className={`${styles.featureContainer} feature-container`}>
                  <img
                    src={PREMIUM_ASSET_PATHS.vc1}
                    alt="Premium voice channel interface"
                    width="100%"
                    loading="lazy"
                    onError={() => handleImageError(PREMIUM_ASSET_PATHS.vc1)}
                    onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.vc1)}
                  />
                </div>
                <div className={styles.featureFocusBg}>
                  <img
                    src={PREMIUM_ASSET_PATHS.bg2}
                    alt="Premium background"
                    width="100%"
                    loading="lazy"
                    onError={() => handleImageError(PREMIUM_ASSET_PATHS.bg2)}
                    onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.bg2)}
                  />
                </div>
              </div>
            </div>
            <div className={styles.featureRight}>
              <div className={styles.featureDetails}>
                <h3>Listen with Friends</h3>
                <p>
                  Your premium subscription is shared with everyone in the group
                  for the duration of the session.
                </p>
              </div>
            </div>
          </div>
          <div className={styles.featureFocus2}>
            <div className={styles.featureLeft}>
              <div className={styles.featureHero}>
                <div
                  className={`${styles.featureContainer} ${styles.image2} feature-container`}
                >
                  <img
                    src={PREMIUM_ASSET_PATHS.app}
                    alt="Premium desktop app"
                    width="100%"
                    loading="lazy"
                    onError={() => handleImageError(PREMIUM_ASSET_PATHS.app)}
                    onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.app)}
                  />
                </div>
                <div className={styles.featureFocusBg}>
                  <img
                    src={PREMIUM_ASSET_PATHS.bg3}
                    alt="Premium background"
                    width="100%"
                    loading="lazy"
                    onError={() => handleImageError(PREMIUM_ASSET_PATHS.bg3)}
                    onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.bg3)}
                  />
                </div>
              </div>
            </div>
            <div className={styles.featureRight}>
              <div className={styles.featureDetails}>
                <h3>Available Everywhere</h3>
                <p>
                  Available as a Discord Bot, Activity, Web and Desktop Apps.
                  (Mobile coming soon)
                </p>
              </div>
            </div>
          </div>
          <div className={styles.albums}>
            <div className={styles.albumsDetails}>
              <h4>All Your Favorite Music</h4>
              <p>
                Unlimited access to your favorite albums, artists, and
                playlists.
              </p>
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album1}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album1)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album1)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album2}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album2)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album2)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album3}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album3)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album3)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album4}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album4)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album4)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album5}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album5)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album5)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album6}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album6)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album6)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album7}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album7)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album7)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album8}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album8)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album8)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album9}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album9)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album9)}
              />
            </div>
            <div className={styles.album}>
              <img
                src={PREMIUM_ASSET_PATHS.album10}
                alt="Album cover"
                width="100%"
                loading="lazy"
                onError={() => handleImageError(PREMIUM_ASSET_PATHS.album10)}
                onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.album10)}
              />
            </div>
          </div>
          <div className={styles.footerText}>
            <div className={styles.footerDudes}>
              <div className={styles.dude}>
                <img
                  src={PREMIUM_ASSET_PATHS.hendrix}
                  alt="Jimi Hendrix"
                  width="100%"
                  loading="lazy"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.hendrix)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.hendrix)}
                />
              </div>
              <div className={styles.dude}>
                <img
                  src={PREMIUM_ASSET_PATHS.petty}
                  alt="Tom Petty"
                  width="100%"
                  loading="lazy"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.petty)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.petty)}
                />
              </div>
              <div className={styles.dude}>
                <img
                  src={PREMIUM_ASSET_PATHS.morrison}
                  alt="Jim Morrison"
                  width="100%"
                  loading="lazy"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.morrison)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.morrison)}
                />
              </div>
              <div className={styles.dude}>
                <img
                  src={PREMIUM_ASSET_PATHS.bowie}
                  alt="David Bowie"
                  width="100%"
                  loading="lazy"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.bowie)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.bowie)}
                />
              </div>
              <div className={styles.dude}>
                <img
                  src={PREMIUM_ASSET_PATHS.lennon}
                  alt="John Lennon"
                  width="100%"
                  loading="lazy"
                  onError={() => handleImageError(PREMIUM_ASSET_PATHS.lennon)}
                  onLoad={() => handleImageLoad(PREMIUM_ASSET_PATHS.lennon)}
                />
              </div>
            </div>
            <h4>Join us on Discord</h4>
            <p className={styles.footerSubtitle}>
              Join us on Discord to get the latest updates and support the bot.
              Premium members can access the premium server for support and
              exclusive perks.
            </p>
            <div className={styles.footerButton}>
              <button
                onClick={() => {
                  window.open(
                    "https://discord.gg/ZtH6rZpa8y",
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
                aria-label="Join Rythm Discord - opens in new tab"
              >
                Rythm Discord
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Premium;
